import { CoverageCountsResponse, DeliveryStrategyKind } from '@ampsec/platform-client';
import {
  CsvExportModule,
  CustomFilterModule,
  IRowNode,
  ModuleRegistry,
  RenderApiModule,
  RowApiModule,
  TextFilterModule,
  TooltipModule,
  ValidationModule,
  type SelectionChangedEvent,
} from 'ag-grid-community';
import {
  ColumnApiModule,
  ColumnMenuModule,
  ColumnsToolPanelModule,
  LicenseManager,
  ServerSideRowModelModule,
  SetFilterModule,
} from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { getCoverageCounts, getConnectorSettings, postEngageNotifications } from 'Apis/library';
import EngageIcon from 'Assets/icons/chat.svg';
import { Typography } from 'Components/atoms';
import { Spinner } from 'react-bootstrap';
import { ActionsBar, AgGridTableHeader, InsightCard } from 'Components/molecules';
import { EmptyState } from 'Components/molecules/EmptyState';
import { EngageModal } from 'Components/organisms';
import EmptyStateIcon from 'Assets/icons/engagement.svg';
import {
  DEFAULT_ERROR_MSG,
  ENGAGE,
  ENGAGE_SUCCESS_MSG,
  ENGAGE_VARIANT,
  ERROR_VARIANT,
} from 'Core-utils/constants/constants';
import { convertFindingViewToNotificationData } from 'Core-utils/helpers/helpers';
import { resolveSsrmSelectedRows } from 'Core-utils/agGridSelection';
import { useApiObjectData } from 'Hooks/useApiObjectData';
import { useConnectorData } from 'Hooks/useConnectorsData';
import { useI18n } from 'Hooks/useI18n';
import { useSearchParamsState } from 'Hooks/useSearchParamsState';
import { ContentfulContext } from 'Rdx/contexts/contentfulContext';
import { ToastsContext } from 'Rdx/contexts/ToastsContext';
import _ from 'lodash';
import { usersOrgFilterFromOrgModel } from 'Core-utils/helpers/orgModelFilters';
import React, { ChangeEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'Rdx/store';
import {
  useRefreshServerSideWhen,
  useAgGridUrlSync,
  type AgGridBaseUrlSyncConfig,
  buildCsv,
  downloadCsv,
  fetchAllPages,
  visibleColumnDefs,
  type FetchPageResult,
} from '../AgGridBase';
import { useSearchParams } from 'react-router-dom';
import { ReactSVG } from 'react-svg';
import {
  AgGridToolingComplianceProps,
  COLUMN_STATE_STORAGE_KEYS,
  DEFAULT_COLUMN_STATE,
  DEFAULT_COL_DEF,
  GRID_CONFIG,
  findingsTheme,
} from './AgGridCompliance.config';
import usersInsightsColumns from './getUsersInsightsColumns';
import { ServerSideDataSource } from './ServerSideDataSource';
import {
  INSIGHT_FILTER_MODELS,
  INSIGHT_LABEL_MAP,
  INSIGHT_DESCRIPTION_MAP,
  InsightKey,
  mapGridStateToUrlParams,
  mapUrlParamsToGridState,
} from './toolingGridUrl';

ModuleRegistry.registerModules([
  ServerSideRowModelModule,
  TextFilterModule,
  CustomFilterModule,
  TooltipModule,
  SetFilterModule,
  ColumnApiModule,
  RenderApiModule,
  RowApiModule,
  ValidationModule,
  CsvExportModule,
  ColumnsToolPanelModule,
  ColumnMenuModule,
]);

const agGridLicenseKey = process.env.REACT_APP_AG_GRID_LICENSE_KEY;
if (agGridLicenseKey) {
  LicenseManager.setLicenseKey(agGridLicenseKey);
}

type InsightTileDefinition = {
  key: InsightKey;
  unit: string;
  denomLabel: string;
  getCount: (data: CoverageCountsResponse) => number;
  getDenominator: (data: CoverageCountsResponse) => number;
};

const INSIGHT_TILES: InsightTileDefinition[] = [
  {
    key: 'usersWithoutAssets',
    unit: 'users',
    denomLabel: 'users',
    getCount: (d) => d.usersWithoutAssets,
    getDenominator: (d) => d.totalUsers,
  },
  {
    key: 'usersInactiveLogin',
    unit: 'users',
    denomLabel: 'users',
    getCount: (d) => d.usersInactiveLogin,
    getDenominator: (d) => d.totalUsers,
  },
  {
    key: 'unassignedAssets',
    unit: 'assets',
    denomLabel: 'assets',
    getCount: (d) => d.unassignedAssets,
    getDenominator: (d) => d.totalAssets,
  },
  {
    key: 'noEdrCoverage',
    unit: 'assets',
    denomLabel: 'assigned assets',
    getCount: (d) => d.assetsWithoutEdrCoverage,
    getDenominator: (d) => d.totalAssignedAssets,
  },
  {
    key: 'noEvmCoverage',
    unit: 'assets',
    denomLabel: 'assigned assets',
    getCount: (d) => d.assetsWithoutEvmCoverage,
    getDenominator: (d) => d.totalAssignedAssets,
  },
];

const AgGridToolingCompliance: React.FC<AgGridToolingComplianceProps> = ({
  title,
  defaultFilter,
  tableBodyHeight,
  emptyStateDescription = 'No tooling coverage records match your filters.',
}) => {
  const gridRef = useRef<any>(null);
  const columnStateLoadedRef = useRef(false);
  const initialGridReadyRef = useRef(false);
  const defaultsHydratedRef = useRef(false);
  const [gridReadySignal, setGridReadySignal] = useState(0);
  const { filter: urlFilter, isUrlLoading, setSearchParamsState } = useSearchParamsState();
  const [, setSearchParams] = useSearchParams();
  const urlFilterRef = useRef(urlFilter);
  urlFilterRef.current = urlFilter;

  const { contentfulData } = useContext(ContentfulContext);
  const { addToast } = useContext(ToastsContext);
  const providerNameIntegrationMap = contentfulData?.providerNameIntegrationMap;
  const installedConnectorMap = useSelector((state: any) => state.installedConnectorMap);
  const organization = useSelector((state: RootState) => state.orgModel.org);

  const { data: tenantSettings, isLoading: isSettingsLoading } = useApiObjectData(
    'CONNECTOR_SETTINGS',
    getConnectorSettings,
    (a) => a,
  );
  const { t } = useI18n();
  const connectorDataHook = useConnectorData();

  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [loadedCount, setLoadedCount] = useState<number | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | undefined>();
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const lastServerSideStateRef = useRef<SelectionChangedEvent<any>['serverSideState']>(null);
  const [actionsShow, setActionsShow] = useState<boolean>(false);
  const [show, setShow] = useState<boolean>(false);
  const [copySecurityTeam, setCopySecurityTeam] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);

  // Coordinated loading state
  const [countsData, setCountsData] = useState<CoverageCountsResponse | null>(null);
  const [countsReady, setCountsReady] = useState(false);
  const [initialGridReady, setInitialGridReady] = useState(false);
  const isFullyLoaded = countsReady && initialGridReady;

  useEffect(() => {
    getCoverageCounts()
      .then((data) => {
        if (data) {
          setCountsData(data);
        }
        setCountsReady(true);
      })
      .catch(() => {
        setCountsReady(true);
      });
  }, []);

  // Hydrate default sort into the URL when none is present (same pattern as AgFindingsGrid).
  // This ensures useAgGridUrlSync always finds a sort to apply, preventing it from clearing
  // the default name-ASC sort that DEFAULT_COLUMN_STATE sets via applyColumnState.
  useEffect(() => {
    if (isUrlLoading || defaultsHydratedRef.current) {
      return;
    }
    defaultsHydratedRef.current = true;
    const hasSortColumn = typeof urlFilter.sortColumn === 'string' && urlFilter.sortColumn.length > 0;
    const hasSortOrder = typeof urlFilter.sortOrder === 'string' && urlFilter.sortOrder.length > 0;
    if (!hasSortColumn || !hasSortOrder) {
      setSearchParamsState({ sortColumn: 'name', sortOrder: 'ASC', ...urlFilter });
    }
  }, [isUrlLoading, urlFilter, setSearchParamsState]);

  const handleRowCountChange = useCallback((filtered: number, total: number) => {
    setTotalCount(total);
    if (!initialGridReadyRef.current) {
      initialGridReadyRef.current = true;
      setInitialGridReady(true);
    }
    // Use effective rendered row count (includes synthetic "Unassigned" row path).
    if (total === 0) {
      gridRef.current?.api?.showNoRowsOverlay();
    } else {
      gridRef.current?.api?.hideOverlay();
    }
  }, []);

  // Default grid view excludes the synthetic Unassigned row.
  // Unassigned assets are shown only via explicit filters/insight tile.
  const defaultGridFilter = useMemo(
    () => ({
      assignmentFilter: 'assigned',
      ...usersOrgFilterFromOrgModel(organization),
      ...(defaultFilter ?? {}),
    }),
    [defaultFilter, organization],
  );

  const { datasource } = ServerSideDataSource({
    defaultFilter: defaultGridFilter,
    onRowCountChange: handleRowCountChange,
    onLastUpdatedAtChange: setLastUpdatedAt,
  });

  useRefreshServerSideWhen(gridRef, organization);

  const datasourceRef = useRef(datasource);
  datasourceRef.current = datasource;
  const stableDatasourceRef = useRef({
    getRows: (params: any) => datasourceRef.current.getRows(params),
  });
  const stableDatasource = stableDatasourceRef.current;

  const { isFetchingConnectors } = connectorDataHook;

  const gridContext = useMemo(() => ({
    tenantSettings,
    providerNameIntegrationMap,
    isLoading: isSettingsLoading || isFetchingConnectors,
    allConnectors: installedConnectorMap,
    connectorDataHook,
    t,
  }), [
    tenantSettings,
    providerNameIntegrationMap,
    isSettingsLoading,
    isFetchingConnectors,
    installedConnectorMap,
    connectorDataHook,
    t,
  ]);

  const toolingUrlSync = useMemo((): AgGridBaseUrlSyncConfig => ({
    enabled: true,
    adapter: {
      fromUrl: (params) => mapUrlParamsToGridState(params as Record<string, any>),
      toUrl: (state) => {
        const params = mapGridStateToUrlParams(state as any);
        // Preserve the active insight param across sort and filter changes
        const currentInsight = urlFilterRef.current.insight as InsightKey | undefined;
        if (currentInsight && INSIGHT_FILTER_MODELS[currentInsight]) {
          params.insight = currentInsight;
        }
        return params;
      },
    },
    debounceMs: 200,
    replaceHistory: true,
    isUrlLoading,
    urlStateRevision: urlFilter,
    getUrlState: () => urlFilterRef.current,
    setUrlState: (next) => setSearchParamsState(next),
  }), [isUrlLoading, urlFilter, setSearchParamsState]);

  const { onFilterOrSortChanged } = useAgGridUrlSync(gridRef, toolingUrlSync, gridReadySignal);

  const onGridReady = useCallback((params: any) => {
    if (!(Array.isArray(usersInsightsColumns) && usersInsightsColumns.length > 0)) {
      return;
    }
    const savedState = localStorage.getItem(COLUMN_STATE_STORAGE_KEYS.server);
    if (savedState) {
      try {
        params.api.applyColumnState({ state: JSON.parse(savedState), applyOrder: true });
      } catch {
        params.api.applyColumnState({ state: DEFAULT_COLUMN_STATE, applyOrder: true });
      }
    } else {
      params.api.applyColumnState({ state: DEFAULT_COLUMN_STATE, applyOrder: true });
    }
    columnStateLoadedRef.current = true;
    setGridReadySignal((s) => s + 1);
  }, []);

  const onSelectionChanged = useCallback((params: SelectionChangedEvent<any>) => {
    lastServerSideStateRef.current = params.serverSideState;
    const selectedData = resolveSsrmSelectedRows(params.api, params.serverSideState);
    setSelectedRows(selectedData);
    setActionsShow(selectedData.length > 0);
  }, []);

  const handleStoreUpdated = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) {
      return;
    }

    let loaded = 0;
    api.forEachNode((node: IRowNode) => {
      if (!node.stub && node.data !== null && node.data !== undefined) {
        loaded++;
      }
    });
    setLoadedCount(loaded);

    const serverSideState = lastServerSideStateRef.current;
    if (!serverSideState || !('selectAll' in serverSideState) || !serverSideState.selectAll) {
      return;
    }
    const data = resolveSsrmSelectedRows(api, serverSideState);
    setSelectedRows(data);
    setActionsShow(data.length > 0);
  }, []);

  const handleColumnEvent = useCallback((params: any) => {
    if (!params?.api || !columnStateLoadedRef.current) {
      return;
    }
    if (
      params.type === 'columnMoved' ||
      params.type === 'columnVisible' ||
      params.type === 'columnPinned' ||
      params.type === 'columnResized' ||
      params.finished
    ) {
      const state = params.api.getColumnState();
      localStorage.setItem(COLUMN_STATE_STORAGE_KEYS.server, JSON.stringify(state));
    }
  }, []);

  const handleFilterSortChanged = useCallback(() => {
    onFilterOrSortChanged();
  }, [onFilterOrSortChanged]);

  const handleResetColumns = useCallback(() => {
    const gridApi = gridRef.current?.api;
    if (gridApi) {
      gridApi.applyColumnState({ state: DEFAULT_COLUMN_STATE, applyOrder: true });
      localStorage.removeItem(COLUMN_STATE_STORAGE_KEYS.server);
      gridApi.setFilterModel({});
      setSearchParamsState({});
    }
  }, [setSearchParamsState]);

  const handlePostProcessPopup = useCallback((params: any) => {
    if (params?.type !== 'filterMenu') {
      return;
    }
    const popupElement = params?.ePopup as HTMLElement | undefined;
    if (!popupElement) {
      return;
    }
    popupElement.style.minWidth = '34rem';
    popupElement.style.width = 'max-content';
    popupElement.style.maxWidth = '120vw';
  }, []);

  const handleExportCsv = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) {
      return;
    }
    // Export the columns the user currently sees (order + visibility), not the static defs.
    // The reason column is hidden, so append it explicitly (last) for the export.
    const visibleCols = visibleColumnDefs(api, usersInsightsColumns);
    const reasonCol = usersInsightsColumns.find((col: any) => (col?.colId ?? col?.field) === 'reason');
    const exportCols = reasonCol && !visibleCols.some((col: any) => (col?.colId ?? col?.field) === 'reason')
      ? [...visibleCols, reasonCol]
      : visibleCols;

    if (selectedRows.length > 0) {
      // buildCsv (rather than api.exportDataAsCsv) so the tooltip columns the defs declare are part
      // of the selected-rows export too — AG Grid can only export its own columns.
      downloadCsv(buildCsv(selectedRows, exportCols, gridContext), 'tooling-coverage.csv');
    } else {
      // Ignore re-entrant clicks while a full export is already in flight.
      if (isExporting) {
        return;
      }
      // SSRM only holds the fetched row blocks, so api.exportDataAsCsv() would miss rows.
      // Page through the datasource (reusing its query context) to fetch every filtered row.
      const fetchAll = async (): Promise<any[]> => {
        const sortModel = api.getSortModel?.() ?? [];
        const filterModel = api.getFilterModel?.() ?? {};
        return fetchAllPages<any>({
          fetchPage: (offset, limit) =>
            new Promise<FetchPageResult<any>>((resolve, reject) => {
              stableDatasource.getRows({
                request: {
                  startRow: offset,
                  // AG Grid's endRow is inclusive (endRow - startRow + 1 rows); this repo's datasources
                  // treat it as exclusive (endRow - startRow). Using endRow = offset + limit - 1 requests
                  // at most `limit` rows under either convention, so fetchAllPages' offset advancement
                  // (capped at the page size) never under-shoots and re-fetches a row at a boundary.
                  endRow: offset + limit - 1,
                  sortModel,
                  filterModel,
                  rowGroupCols: [],
                  valueCols: [],
                  pivotCols: [],
                  pivotMode: false,
                  groupKeys: [],
                },
                success: (params: any) => {
                  resolve({
                    rows: params?.rowData ?? [],
                    total: params?.rowCount,
                    // The datasource reports how many *source* rows the page consumed (its
                    // display rows may be expanded), so the fetch-all loop advances the offset
                    // by that and does not skip source rows.
                    sourceCount: params?.sourceCount,
                  });
                },
                fail: () => reject(new Error('Failed to fetch usersInsights')),
                // Pass the grid api/context so the datasource can reuse them if it ever needs to;
                // there is no node in this on-demand export context.
                context: gridContext,
                parentNode: undefined as any,
                api,
              });
            }),
        });
      };

      setIsExporting(true);
      fetchAll()
        .then((rows) => {
          const csv = buildCsv(rows, exportCols, gridContext);
          downloadCsv(csv, 'tooling-coverage.csv');
        })
        .catch(() => {
          addToast({ severity: 'error', body: DEFAULT_ERROR_MSG });
        })
        .finally(() => {
          setIsExporting(false);
        });
    }
  }, [selectedRows, isExporting, stableDatasource, usersInsightsColumns, gridContext, addToast]);

  const applyInsightTileFilters = useCallback(
    (insightKey: InsightKey) => {
      // setSearchParamsState replaces the whole query string, so rebuild it from the keys the
      // tile owns: keep the sort (dropping it would reset the grid's sort on hydration) and drop
      // every filter param. mapUrlParamsToGridState expands the insight last, so it wins for its
      // own column, but any other coverage filter left in the URL (e.g. a stale "Missing EDR"
      // column filter) would be ANDed with the tile's filter and empty the result set. The
      // insight badge's dismiss button clears the query string the same way.
      setSearchParamsState({
        sortColumn: urlFilterRef.current.sortColumn,
        sortOrder: urlFilterRef.current.sortOrder,
        insight: insightKey,
      });
    },
    [setSearchParamsState],
  );

  const handleClearInsight = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) {
      return;
    }
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  class EngagementsSelectorHandler {
    handleActionsClose = () => {
      setSelectedRows([]);
      setActionsShow(false);
      if (gridRef.current?.api) {
        gridRef.current.api.deselectAll();
      }
    };

    handleEngageClick = () => setShow((s) => !s);

    handleClose = () => setShow(false);

    getToastBody = (variant: string): string => {
      const variantMessages: Record<string, string> = {
        [ENGAGE_VARIANT]: ENGAGE_SUCCESS_MSG,
        [ERROR_VARIANT]: DEFAULT_ERROR_MSG,
      };
      return variantMessages?.[variant] || variant || '';
    };

    handleSend = (text: string, strategy: DeliveryStrategyKind, includeFinding = false) => {
      const NotificationDataArray = selectedRows.map((selectedRow) =>
        convertFindingViewToNotificationData(text, copySecurityTeam, strategy, includeFinding, undefined, selectedRow),
      );

      postEngageNotifications(NotificationDataArray)
        .then((response) => {
          addToast({
            severity: response?.error ? 'error' : 'success',
            body: this.getToastBody(response?.error || ENGAGE_SUCCESS_MSG),
          });
        })
        .catch((error) => {
          addToast({
            severity: 'error',
            body: t(error.response.data.message),
          });
        });
      setShow(false);
      setActionsShow(false);
      setSelectedRows([]);
      if (gridRef.current?.api) {
        gridRef.current.api.deselectAll();
      }
    };

    handleCopySecurityTeamCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
      setCopySecurityTeam(event.target.checked);
    };
  }
  const engagementsSelectorHandler = new EngagementsSelectorHandler();

  const actions = [
    {
      label: ENGAGE,
      icon: (
        <ReactSVG
          src={EngageIcon}
          className="data-grid__svg-react-icon d-flex"
        />
      ),
      onClick: engagementsSelectorHandler.handleEngageClick,
    },
  ];

  const getRowHeight = (params: any) => {
    const baseHeight = GRID_CONFIG.rowHeight;
    const row = params.data;
    if (row && Array.isArray(row.assets) && row.assets.length > 1) {
      return baseHeight + (row.assets.length - 1) * 40;
    }
    return baseHeight;
  };

  const activeInsightKey = urlFilter.insight as InsightKey | undefined;
  const activeInsightLabel = activeInsightKey ? INSIGHT_LABEL_MAP[activeInsightKey] : undefined;

  return (
    <div className="h-100 d-flex flex-column tooling-compliance__container ag-grid-theme--amp-dark">
      <AgGridTableHeader
        title={title}
        lastUpdatedAt={lastUpdatedAt}
        rightExtras={
          isExporting || (typeof loadedCount === 'number' && typeof totalCount === 'number') ? (
            <>
              {isExporting ? (
                <span className="ag-grid-export-status d-flex align-items-center gap-2">
                  <Spinner animation="border" size="sm" />
                  <Typography variant="body10" color="text-low-emphasis">
                    Exporting…
                  </Typography>
                </span>
              ) : null}
              {typeof loadedCount === 'number' && typeof totalCount === 'number' ? (
                <Typography variant="body10" color="text-low-emphasis" className="ag-grid-table-header__results-count">
                  <span className="ag-grid-table-header__results-count-number">{loadedCount}</span> results of{' '}
                  <span className="ag-grid-table-header__results-count-number">{totalCount}</span> total
                </Typography>
              ) : null}
            </>
          ) : null
        }
        actionItems={[
          {
            label: selectedRows.length > 0 ? `Export ${selectedRows.length} Rows` : 'Export CSV',
            onClick: handleExportCsv,
          },
          { label: 'Reset Columns', onClick: handleResetColumns },
        ]}
      />

      {activeInsightLabel && (
        <div className="insight-active-badge">
          <span className="insight-active-badge__label">Active filter: {t(activeInsightLabel)}</span>
          <button
            type="button"
            className="insight-active-badge__dismiss"
            onClick={handleClearInsight}
            aria-label="Clear insight filter"
          >
            ×
          </button>
        </div>
      )}

      <div className="tooling-compliance__loadable" style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column' }}>
        {!isFullyLoaded && (
          <div className="tooling-compliance__loading-overlay" aria-busy="true">
            <div className="tooling-compliance__loading-spinner" />
          </div>
        )}

        <div className="insight-tiles">
          {INSIGHT_TILES.map((tile) => {
            const count = countsData ? tile.getCount(countsData) : null;
            const denominator = countsData ? tile.getDenominator(countsData) : null;
            const pct = count !== null && denominator ? Math.round((count / denominator) * 100) : null;
            const isActive = activeInsightKey === tile.key;

            return (
              <InsightCard
                key={tile.key}
                isActive={isActive}
                onClick={() => applyInsightTileFilters(tile.key)}
                ariaLabel={`Apply ${INSIGHT_LABEL_MAP[tile.key]} filter`}
              >
              <InsightCard.Title>
                <Typography variant="subtitle1" color="text-high-emphasis">{t(INSIGHT_LABEL_MAP[tile.key])}</Typography>
              </InsightCard.Title>
                <InsightCard.Value>
                  <InsightCard.ValueNumber>
                    {count !== null ? count : '—'}{' '}
                  </InsightCard.ValueNumber>
                  <InsightCard.ValueUnit>{tile.unit}
                  {pct !== null && (
                    <span> ({pct}%) </span>
                    )}
                  </InsightCard.ValueUnit>
                </InsightCard.Value>
                <InsightCard.Description>{t(INSIGHT_DESCRIPTION_MAP[tile.key])}</InsightCard.Description>
              </InsightCard>
            );
          })}
        </div>

        <div
          className="d-flex flex-column gap-3 pt-4 pb-4 h-100 security-findings tooling-compliance__grid-wrapper flex-grow-1"
          style={{ minHeight: tableBodyHeight || GRID_CONFIG.minHeight }}
        >
          <AgGridReact
              ref={gridRef}
              theme={findingsTheme}
              columnDefs={usersInsightsColumns}
              context={gridContext}
              rowModelType="serverSide"
              serverSideDatasource={stableDatasource}
              getRowId={(params) => String(params.data?.__rowId ?? params.data?.id ?? '')}
              domLayout="normal"
              suppressAggFuncInHeader
              animateRows
              defaultColDef={DEFAULT_COL_DEF}
              getRowHeight={getRowHeight}
              headerHeight={GRID_CONFIG.headerHeight}
              rowSelection={{
                mode: 'multiRow',
              }}
              onSelectionChanged={onSelectionChanged}
              onModelUpdated={handleStoreUpdated}
              onColumnResized={handleColumnEvent}
              onColumnMoved={handleColumnEvent}
              onColumnVisible={handleColumnEvent}
              onColumnPinned={handleColumnEvent}
              onFilterChanged={handleFilterSortChanged}
              onSortChanged={handleFilterSortChanged}
              cacheBlockSize={GRID_CONFIG.cacheBlockSize}
              onGridReady={onGridReady}
              sideBar={GRID_CONFIG.sideBar}
              postProcessPopup={handlePostProcessPopup}
              noRowsOverlayComponent={EmptyState}
              noRowsOverlayComponentParams={{
                ImageSrc: EmptyStateIcon,
                noDescription: false,
                description: emptyStateDescription,
              }}
            />
        </div>
      </div>

      {selectedRows.length > 0 && actionsShow && (
        <ActionsBar
          selectedFindings={selectedRows.length}
          actions={actions}
          show={selectedRows.length > 0 && actionsShow}
          onActionsClose={engagementsSelectorHandler.handleActionsClose}
        />
      )}
      {show && (
        <EngageModal
          show={show}
          onClose={engagementsSelectorHandler.handleClose}
          onSend={engagementsSelectorHandler.handleSend}
          copySecurityTeam={copySecurityTeam}
          onChangeCheckbox={engagementsSelectorHandler.handleCopySecurityTeamCheckboxChange}
          findingsData={false}
        />
      )}
    </div>
  );
};

export default AgGridToolingCompliance;
